import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Dispatch, connect } from 'react-redux';
import { Button, Label, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Loader from 'react-spinners/ClockLoader';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { GridWrapper, HeaderWrapper } from '../styles';
import { IPendingRegistrationDonorsResponse, WatrActionCreators } from '@nkr/store/index';
import { IBaseResponse, IPendingRegistrationDonor } from '@nkr/models/index';
import { OrganType, DonationType, WageAndTravelType } from '@nkr/enums/index';
import { Utility } from '@nkr/services/utility';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import OverlayMenuButton, { IMenuItem } from '@nkr/components/overlay-menu-button';

enum PendingRegistrationAction {
    ResendInvitation = 1,
    CancelRequest = 2
}

interface IProps {
    centerId: number,
    organTypes: OrganType[],
    onGridReady?: (params: GridReadyEvent) => void
}

interface IDispatchProps {
    getPendingRegistrationDonors: (centerId: number, organType?: OrganType) => Promise<IPendingRegistrationDonorsResponse>,
    resendInvitation: (requestId: number) => Promise<IBaseResponse>,
    cancelRequest: (requestId: number) => Promise<IBaseResponse>
}

type TProps = IProps & IDispatchProps;

interface IState {
    donors: IPendingRegistrationDonor[],
    fetching: boolean,
    cancelRequest: {
        show: boolean,
        data: IPendingRegistrationDonor
    }
}

const donationTypeAbbreviation = (donationType: number, isDsdOnly?: boolean) => {
    switch (donationType) {
        case DonationType.DirectedDonor: return 'PD';
        case DonationType.StandardVoucher: return 'SVD';
        case DonationType.NonDirectedDonor: return 'NDD';
        case DonationType.FamilyVoucher: return 'FVD';
        case DonationType.RemoteDirect: return 'RDD';
        case DonationType.KflInternalDirect: return 'KFL IDD';
        case DonationType.KflRemoteDirect: return 'KFL RDD';
        case DonationType.Bridge: return 'BD';
        case DonationType.NkrInternalDirect: return 'NKR IDD';
        case DonationType.SwapSaver: return 'SS';
        case DonationType.RealTimeSwapFailure: return 'RTSF';
        default: return isDsdOnly ? 'DSD' : 'Unknown';
    }
}

class PendingRegistrationList extends React.Component<TProps, IState> {
    readonly state: IState = {
        donors: [],
        fetching: true,
        cancelRequest: {
            show: false,
            data: null
        }
    }

    componentDidMount = () => {
        this.getData();
    }

    componentDidUpdate = (prevProps: TProps) => {
        if (prevProps.centerId != this.props.centerId || !_.isEqual(prevProps.organTypes, this.props.organTypes)) {
            this.getData();
        }
    }

    getData = () => {
        let organTypes = this.props.organTypes || [];
        let organType = organTypes.length == 1 ? organTypes[0] : undefined;

        this.setState({ fetching: true }, () => {
            this.props.getPendingRegistrationDonors(this.props.centerId, organType).then(resp => {
                this.setState({
                    donors: resp.data.success ? resp.data.results : [],
                    fetching: false
                });
            });
        });
    }

    handleResendInvite = (requestId: number) => {
        this.props.resendInvitation(requestId).then(resp => {
            if (resp.data.success) {
                toast.success('Registration invitation sent.');
            } else {
                toast.error(resp.data.results || 'Unable to send the registration invitation.');
            }
        }).catch(() => {
            toast.error('Unable to send the registration invitation.');
        });
    }

    handleCancelRequestClick = (data: IPendingRegistrationDonor) => {
        this.setState({ cancelRequest: { show: true, data } });
    }

    handleActionSelect = (id: number, action: PendingRegistrationAction) => {
        switch (action) {
            case PendingRegistrationAction.ResendInvitation:
                this.handleResendInvite(id);
                break;

            case PendingRegistrationAction.CancelRequest:
                let data = this.state.donors.find(x => x.requestId == id);
                if (data) {
                    this.handleCancelRequestClick(data);
                }
                break;
        }
    }

    handleCancelRequestClose = () => {
        this.setState({ cancelRequest: { show: false, data: null } });
    }

    handleCancelRequestConfirm = () => {
        let requestId = this.state.cancelRequest.data.requestId;

        this.props.cancelRequest(requestId).then(resp => {
            if (resp.data.success) {
                toast.success('Registration cancelled.');
                this.setState({
                    donors: this.state.donors.filter(x => x.requestId != requestId),
                    cancelRequest: { show: false, data: null }
                });
            } else {
                toast.error(resp.data.results || 'Unable to cancel the registration.');
                this.setState({ cancelRequest: { show: false, data: null } });
            }
        }).catch(() => {
            toast.error('Unable to cancel the registration.');
            this.setState({ cancelRequest: { show: false, data: null } });
        });
    }

    onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();

        if (this.props.onGridReady) {
            this.props.onGridReady(params);
        }
    }

    onRowDataChanged = (params: RowDataChangedEvent) => {
        params.api.sizeColumnsToFit();
    }

    getColumnDefs = (): ColDef[] => [
        {
            colId: '1',
            field: 'rank',
            headerName: 'Rank',
            headerComponentFramework: (props: IHeaderParams) => <div>R#</div>,
            sortable: false,
            width: 50,
            pinned: 'left',
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            field: 'organType',
            headerName: 'Organ Type',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    <HeaderWrapper>Organ<br />Type</HeaderWrapper>
                </SortableHeaderComponent>,
            width: 75,
            valueGetter: (params: ValueGetterParams) => {
                let value = (params.data as IPendingRegistrationDonor).organType;
                return value == null ? 'Unknown' : value == OrganType.Liver ? 'Liver' : 'Kidney';
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPendingRegistrationDonor;
                let organType = data.organType == null ? '--' : data.organType == OrganType.Liver ? 'L' : 'K';
                return <Label bsStyle={data.organType == null ? 'default' : data.organType == OrganType.Liver ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
            }
        },
        {
            field: 'donationType',
            headerName: 'Donation Type',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    <HeaderWrapper>Donation<br /> Type</HeaderWrapper>
                </SortableHeaderComponent>,
            width: 100,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IPendingRegistrationDonor;
                return donationTypeAbbreviation(data.donationType, data.isDsdOnly);
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPendingRegistrationDonor;
                let label = donationTypeAbbreviation(data.donationType, data.isDsdOnly);
                let bsStyle = label == 'DSD' ? 'warning' : label == 'Unknown' ? 'danger' : 'info';
                return <Label bsStyle={bsStyle} style={{ fontSize: '100%' }}>{label}</Label>;
            }
        },
        {
            field: 'watrRequestType',
            headerName: 'Request Type',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    <HeaderWrapper>Request<br /> Type</HeaderWrapper>
                </SortableHeaderComponent>,
            width: 100,
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPendingRegistrationDonor;
                let style = 'danger';
                let label = 'Unknown';

                if (data.watrRequestType == WageAndTravelType.ReimburseWagesOnly) {
                    style = 'success';
                    label = 'Wages';
                } else if (data.watrRequestType == WageAndTravelType.ReimburseTravelOnly) {
                    style = 'info';
                    label = 'Expenses';
                } else if (data.watrRequestType == WageAndTravelType.ReimburseBoth) {
                    style = 'default';
                    label = 'Both';
                }

                return <Label bsStyle={style} style={{ fontSize: '100%' }}>{label}</Label>;
            }
        },
        {
            field: 'donorAlias',
            headerName: 'Donor Alias',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    <HeaderWrapper>Donor Alias</HeaderWrapper>
                </SortableHeaderComponent>,
            width: 120,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPendingRegistrationDonor;

                if (!data.isDsdOnly && data.donorId > 0) {
                    return (
                        <a href={`/center/${this.props.centerId}/donor/${data.donorId}/profile/donor-data`}>
                            {data.donorAlias}
                        </a>
                    );
                }

                return (
                    <a href={`/center/${this.props.centerId}/donors/donor-shield-direct/edit-donor/${data.id}`}>
                        {data.donorAlias}
                    </a>
                );
            }
        },
        {
            field: 'donorName',
            headerName: 'Name',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    <HeaderWrapper>Name</HeaderWrapper>
                </SortableHeaderComponent>,
            width: 170
        },
        {
            field: 'donationDate',
            headerName: 'Donation Date',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    <HeaderWrapper>Donation<br /> Date</HeaderWrapper>
                </SortableHeaderComponent>,
            width: 105,
            valueGetter: (params: ValueGetterParams) =>
                moment(params.data.donationDate).isValid()
                    ? moment(params.data.donationDate).format('MM/DD/YYYY')
                    : null,
            comparator: Utility.dateComparator,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPendingRegistrationDonor;
                let value = moment(data.donationDate).isValid() ? moment(data.donationDate).format('MM/DD/YYYY') : 'Pending';

                return <>{value}</>
            }
        },
        {
            field: 'isInternational',
            headerName: 'International Donor',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    <HeaderWrapper>International<br />Donor</HeaderWrapper>
                </SortableHeaderComponent>,
            width: 75,
            valueGetter: (params: ValueGetterParams) => {
                let value = (params.data as IPendingRegistrationDonor).isInternational;
                return value == null ? 'Unknown' : value ? 'Yes' : 'No';
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let value = (params.data as IPendingRegistrationDonor).isInternational;
                let label = value == null ? 'Unknown' : value ? 'Yes' : 'No';
                let bsStyle = value == null ? 'default' : value ? 'success' : 'default';
                return <Label bsStyle={bsStyle} style={{ fontSize: '100%' }}>{label}</Label>;
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    <HeaderWrapper>Status</HeaderWrapper>
                </SortableHeaderComponent>,
            width: 130,
            sortable: false,
            cellRendererFramework: () => <Label bsStyle='warning' style={{ fontSize: '100%' }}>Pending Profile</Label>
        },
        {
            field: 'actionId',
            headerName: 'Actions',
            headerComponentFramework: () => <div style={{ width: '100%', textAlign: 'center' }}>Actions</div>,
            width: 145,
            cellClass: 'action-menu',
            headerClass: 'action-menu-header',
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPendingRegistrationDonor;
                let menu: IMenuItem[] = [
                    { id: data.requestId, action: PendingRegistrationAction.ResendInvitation, include: true, selectable: true, element: <>Resend Invitation</> },
                    { id: `div-${data.requestId}`, action: -1, include: true, selectable: false, divider: true, element: null },
                    { id: data.requestId, action: PendingRegistrationAction.CancelRequest, include: true, selectable: true, element: <>Cancel Request</> }
                ];

                return (
                    <div className='watr-action-cell watr-action-cell--solo'>
                        <div className='watr-action-cell__menu'>
                            <OverlayMenuButton
                                menu={menu}
                                bsStyle='primary'
                                bsSize='xsmall'
                                title='Actions'
                                listItemStyle={{ padding: '2px 10px', fontSize: 14 }}
                                includeCaret={true}
                                onSelect={this.handleActionSelect}
                            />
                        </div>
                    </div>
                );
            }
        }
    ];

    columnDefs: ColDef[] = this.getColumnDefs();

    render = () => {
        if (this.state.fetching) {
            return <div style={{ textAlign: 'center', padding: 40 }}><Loader color='#a1a1a1' /></div>;
        }

        return (
            <>
                <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' height='70vh'>
                    <AgGridReact
                        defaultColDef={{ sortable: true }}
                        columnDefs={this.columnDefs}
                        rowData={this.state.donors}
                        headerHeight={40}
                        rowHeight={32}
                        enableCellTextSelection={true}
                        suppressCellSelection={true}
                        rowStyle={{ overflow: 'visible', cursor: 'pointer' }}
                        onGridReady={this.onGridReady}
                        onRowDataChanged={this.onRowDataChanged}
                        noRowsOverlayComponentFramework={() => <div>No donors are pending registration.</div>}
                    >
                    </AgGridReact>
                </GridWrapper>

                <Modal backdrop='static' show={this.state.cancelRequest.show} onHide={this.handleCancelRequestClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Cancel Request</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to cancel the pending registration for <strong>{this.state.cancelRequest.data?.donorName}</strong>?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button bsStyle='danger' onClick={this.handleCancelRequestConfirm}>Yes, Cancel Request</Button>
                        <Button onClick={this.handleCancelRequestClose}>Cancel</Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        getPendingRegistrationDonors: (centerId, organType) => {
            return dispatch(WatrActionCreators.getPendingRegistrationDonors(centerId, organType));
        },
        resendInvitation: (requestId) => {
            return dispatch(WatrActionCreators.resendInvitation(requestId));
        },
        cancelRequest: (requestId) => {
            return dispatch(WatrActionCreators.cancelRequest(requestId));
        }
    };
}

export default connect<{}, IDispatchProps, IProps>(
    null,
    mapDispatchToProps)(PendingRegistrationList);
